import { BrandIdKeys } from '@seek/brand-types';
import {
  type BrandCallback,
  BrandSelector,
  type BrandSelectorResult,
} from '@seek/branding-assets';
import { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { createAuthService } from 'src/app/shared/authService/authService';
import { TEALIUM_HAS_BRAND_ASSETS } from 'src/app/shared/constants/dispatcherId';
import {
  BRANDING_DISPLAYED,
  BRAND_CREATE_FROM_CAJA,
  BRAND_EDIT_FROM_CAJA,
  BRAND_UPLOAD_SUCCEEDED,
  CREATE_NEW_BRAND_PRESSED,
  EXISTING_BRAND_PRESSED,
  LOGO_ADD_PRESSED,
  LOGO_PRESSED,
  LOGO_REPLACE_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import {
  useFeatureToggleData,
  FEATURE_CART_AND_PAY_AND_POST_V2,
} from 'src/app/shared/featureToggle';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { isProd } from 'src/app/utils/environment';
import { getProductFeatureByType } from 'src/app/utils/getProductFeatureByType';
import { getSelectedProduct } from 'src/app/utils/getSelectedProduct';
import { log } from 'src/app/utils/logging';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import dispatchTealium from 'src/app/v1/state/dispatcher/tealium';
import { actionUpdateBrand } from 'src/app/v1/state/modules/formData/brand';
import { BrandingAlert } from 'src/app/v1/views/components/BrandingAlert';
import type { AdProduct } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

export const Branding = (props: {
  customFeatures?: AdProduct['features'] | undefined;
}) => {
  const {
    isConfidential,
    brandId,
    selectedProductId,
    productSet,
    productFeatures,
  } = useSelector(
    (state: AppState) => ({
      isConfidential: state.formData.isConfidential,
      brandId: state.formData.brand?.[BrandIdKeys.id],
      seekAdvertiserId: state.session?.seekAdvertiserId,
      selectedProductId: state.formData.selectedProductId,
      productSet: state.session.productSet,
      productFeatures: state.session.productFeatures,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const { getToken } = createAuthService();

  const { cartFeatures } = useShoppingCart();

  const isCartEnabled = useFeatureToggleData(FEATURE_CART_AND_PAY_AND_POST_V2);

  const onAddLogoClick = useCallback(() => {
    const eventData: TrackingEvent = {
      eventName: LOGO_ADD_PRESSED,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  }, []);

  const onReplaceLogo = useCallback(() => {
    const eventData: TrackingEvent = {
      eventName: LOGO_REPLACE_PRESSED,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  }, []);

  const onSelectBrandTabClick = useCallback(() => {
    const eventData: TrackingEvent = {
      eventName: EXISTING_BRAND_PRESSED,
      brandingFlow: BRAND_EDIT_FROM_CAJA,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  }, []);

  const onCreateBrandTabClick = useCallback(() => {
    const eventData: TrackingEvent = {
      eventName: CREATE_NEW_BRAND_PRESSED,
      brandingFlow: BRAND_CREATE_FROM_CAJA,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  }, []);

  const onNewBrandUpdated = useCallback(() => {
    const eventData: TrackingEvent = {
      eventName: BRAND_UPLOAD_SUCCEEDED,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  }, []);

  const onBrandsLoaded = useCallback(
    ({
      numberOfBrandsDisplayed,
      numberOfBrands,
    }: {
      numberOfBrandsDisplayed: number;
      numberOfBrands: number;
    }) => {
      const eventData: TrackingEvent = {
        eventName: BRANDING_DISPLAYED,
        brandDisplayedCount: numberOfBrandsDisplayed,
        brandsTotal: numberOfBrands,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    },

    [],
  );

  const onBrandSelected: BrandCallback = useCallback(
    ({ data, error }: BrandSelectorResult) => {
      if (!error) {
        if (data?.legacyId) {
          dispatch(actionUpdateBrand(data));

          dispatch(
            dispatchTealium(TEALIUM_HAS_BRAND_ASSETS, {
              hasCoverImage: Boolean(data?.assets.cover),
              hasLogo: Boolean(data?.assets.logo),
            }),
          );

          const eventData: TrackingEvent = {
            eventName: LOGO_PRESSED,
            position: data?.position,
          };
          getTrackingAdapter.sendLinkEvent(eventData);
        }
      } else {
        log.error('Branding | onBrandSaved', { error });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  const selectedProduct = getSelectedProduct(selectedProductId, productSet);

  // TODO FOR EDIT FLOW
  // HOW to use cartFeatures
  // upgrade
  const customProductBranding = Boolean(props.customFeatures)
    ? getProductFeatureByType({
        featureType: 'Branding',
        productFeatures: props.customFeatures,
      })
    : undefined;

  // draft
  const selectedProductBranding = getProductFeatureByType({
    featureType: 'Branding',
    productFeatures: isCartEnabled ? cartFeatures : selectedProduct?.features,
  });

  // TODO FOR EDIT FLOW
  // HOW to use cartFeatures
  // edit
  const jobProductBranding = getProductFeatureByType({
    featureType: 'Branding',
    productFeatures,
  });
  const showCoverImage = Boolean(
    (customProductBranding ?? jobProductBranding ?? selectedProductBranding)
      ?.hasCoverImage,
  );

  return isConfidential ? (
    <BrandingAlert />
  ) : (
    <BrandSelector
      getToken={getToken}
      isProd={isProd()}
      brandId={brandId}
      onAddLogoClick={onAddLogoClick}
      onBrandsLoaded={onBrandsLoaded}
      onBrandSelected={onBrandSelected}
      onCreateBrandTabClick={onCreateBrandTabClick}
      onNewBrandUpdated={onNewBrandUpdated}
      onReplaceLogoClick={onReplaceLogo}
      onSelectBrandTabClick={onSelectBrandTabClick}
      showCoverImage={showCoverImage}
      showCoverImageText={true}
      tileRowCount={3}
    />
  );
};
